import { useEffect, useId } from 'react'
import type { JSX } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import type { ErrorLike } from '@apollo/client'
import { clsx } from 'clsx'
import { format } from 'date-fns'

import {
  Button,
  ChevronRightIcon,
  Flex,
  Notice,
  PlusIcon,
  Skeleton,
  Text,
  TrendingUpIcon,
  useLocalStorage,
} from '@buffer-mono/popcorn'

import { NewPostComposerTrigger } from '~publish/components/NewPostComposerTrigger'
import { ChartErrorState } from '~publish/pages/Insights/components'
import type { SummaryMetricItem } from '~publish/pages/Insights/components'

import { WeeklyPulseCard } from './WeeklyPulseCard'

import almostReadyArt from './assets/almost-ready-illustration.svg'
import styles from './WeeklyPulse.module.css'
import { SectionHeader } from '../SectionHeader'

const EMPTY_STATE_CTA = 'publish-home-weeklyPulse-createPost-1'

export const WEEKLY_PULSE_LAST_BODY_STORAGE_KEY = 'weekly-pulse-last-body'

/** Which body the module last resolved to for this browser's account. */
type WeeklyPulseBodyKind = 'cards' | 'empty'

const SKELETON_KEYS = [
  'posts',
  'followers',
  'comments',
  'fourth',
  'fifth',
  'sixth',
]

interface WeeklyPulseProps {
  metrics: SummaryMetricItem[]
  loading: boolean
  refreshing?: boolean
  error?: ErrorLike
  comparisonLabel: string
  skeletonCount?: number
}

function formatWeekRange(start: Date, end: Date): string {
  return `${format(start, 'MMM d')} to ${format(end, 'MMM d')}`
}

export function WeeklyPulse({
  metrics,
  loading,
  refreshing = false,
  error,
  comparisonLabel,
  skeletonCount = 3,
  startDate,
  endDate,
}: WeeklyPulseProps & { startDate: Date; endDate: Date }): JSX.Element {
  const headingId = useId()

  return (
    <section aria-labelledby={headingId} className={styles.section}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <SectionHeader id={headingId}>Weekly Pulse</SectionHeader>
          <Text as="span" size="sm" color="subtle">
            <span className={styles.leadingSeparator}>· </span>
            {formatWeekRange(startDate, endDate)} · Compared to previous week
          </Text>
        </div>
        <Button
          as={RouterLink}
          to="/insights"
          size="small"
          variant="tertiary"
          className={styles.seeAll}
        >
          Go to Insights
          <ChevronRightIcon />
        </Button>
      </div>
      <WeeklyPulseBody
        metrics={metrics}
        loading={loading}
        refreshing={refreshing}
        error={error}
        comparisonLabel={comparisonLabel}
        skeletonCount={skeletonCount}
      />
    </section>
  )
}

/**
 * Loading placeholder for the metric card grid. The line heights mirror the
 * real card's label (`Text` sm) and stat (`Heading` lg) line boxes so the
 * swap to loaded cards does not move the sections below.
 */
export function WeeklyPulseSkeleton({
  skeletonCount = 3,
}: {
  skeletonCount?: number
}): JSX.Element {
  return (
    <ul className={styles.grid}>
      {SKELETON_KEYS.slice(0, skeletonCount).map((key) => (
        <li key={key}>
          <Flex direction="column" gap="xs" className={styles.card}>
            <Skeleton height={21} width="55%" />
            <Skeleton height={25} width="45%" />
          </Flex>
        </li>
      ))}
    </ul>
  )
}

function WeeklyPulseBody({
  metrics,
  loading,
  refreshing,
  error,
  comparisonLabel,
  skeletonCount = 3,
}: WeeklyPulseProps): JSX.Element {
  const [lastBody, setLastBody] = useLocalStorage<WeeklyPulseBodyKind>(
    WEEKLY_PULSE_LAST_BODY_STORAGE_KEY,
    'cards',
  )

  // Remember which body this account resolved to. The empty state is taller
  // than the card skeleton, so the next load can reserve the right geometry.
  useEffect(() => {
    if (loading || error) return
    setLastBody(metrics.length === 0 ? 'empty' : 'cards')
  }, [loading, error, metrics.length, setLastBody])

  if (loading) {
    // The empty state is fully static, so when this account showed it last
    // time, render it as the loading state — if the query resolves to the
    // empty state again (the common case), nothing on the page moves.
    if (lastBody === 'empty') {
      return <WeeklyPulseEmptyState />
    }
    return <WeeklyPulseSkeleton skeletonCount={skeletonCount} />
  }

  if (error) {
    return <ChartErrorState size="small" />
  }

  if (metrics.length === 0) {
    return <WeeklyPulseEmptyState />
  }

  return (
    <ul
      className={clsx(styles.grid, refreshing && styles.refreshing)}
      aria-busy={refreshing}
    >
      {metrics.map((metric) => (
        <li key={metric.type}>
          <WeeklyPulseCard metric={metric} comparisonLabel={comparisonLabel} />
        </li>
      ))}
    </ul>
  )
}

function WeeklyPulseEmptyState(): JSX.Element {
  return (
    <Notice
      variant="info"
      icon={<TrendingUpIcon />}
      className={styles.emptyStateNotice}
    >
      <Notice.Heading>Almost ready!</Notice.Heading>
      <Notice.Text>
        Create at least 5 posts so we can start analyzing your performance and
        uncover patterns.
      </Notice.Text>
      <Notice.Actions>
        <NewPostComposerTrigger cta={EMPTY_STATE_CTA}>
          <Button size="small">
            <PlusIcon />
            Create Post
          </Button>
        </NewPostComposerTrigger>
      </Notice.Actions>
      <img
        src={almostReadyArt}
        alt=""
        aria-hidden
        className={styles.emptyStateArt}
      />
    </Notice>
  )
}
